import {
    ColumnFilterOption,
    ViewConfig,
} from '../../../models/view-config.interface';
import { ConsigneeBundleGroupsPaginatedService } from '@app/consignee/services/consignee-bundle-groups-paginated.service';
import { AppStore } from '@app/stores/appStore';
import { ColumnCreator } from '../column-creator';
import { FilterKeyCreator } from '../filter-key-creator';
import { BundleGroupStatusType } from 'api/addons-v3/models';
import { BundleGroupStatusDisplayComponent } from '@app/shared/components/table-cell-components';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { FolderData, TableRowData } from '@app/shared/models/table-data-types';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { BundleGroupData } from '@app/stores/bundleGroupsStore';
import { DeliveryNoteBundlesService } from 'api/services';
import { ConsigneeDeliveryNoteBundle } from 'api/models';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import {
    SelectionStore,
    filterBundleGroupData,
    hasStatus,
    hasReadOnly,
} from '@app/stores/selectionStore';

export interface ConsigneeBundleGroupsConfigDeps {
    consigneeBundleGroupsPaginatedService: ConsigneeBundleGroupsPaginatedService;
    deliveryNoteBundlesService: DeliveryNoteBundlesService;
    appStore: InstanceType<typeof AppStore>;
    actionsService: ConsigneeViewActionsService;
    selectionStore: InstanceType<typeof SelectionStore>;
    toastService: ToastService;
    translateService: TranslateService;
}

/**
 * Maps a bundle loaded via the consignee bundle endpoint onto the shape the
 * shared bundle columns and bundle actions expect (see FolderData).
 */
function toGroupedBundleRow(
    bundle: ConsigneeDeliveryNoteBundle,
    group: BundleGroupData
): FolderData {
    return {
        ...bundle,
        accessSiteKey: group.orgSiteKey,
        orgSiteKey: group.orgSiteKey,
        numberOfDeliveryNotes: bundle.deliveryNotes?.length ?? 0,
        checkoutDate: bundle.dateShipped,
        read_only: group.read_only ?? false,
    } as unknown as FolderData;
}

/**
 * The bundle group lifecycle, in the order a group passes through it. Same
 * translation keys the status cell renders (see
 * {@link BundleGroupStatusDisplayComponent}), so the chip and the column agree.
 */
const BUNDLE_GROUP_STATUS_FILTER_OPTIONS: ColumnFilterOption[] = [
    BundleGroupStatusType.Open,
    BundleGroupStatusType.CheckinStarted,
    BundleGroupStatusType.CheckinFinished,
].map(status => ({
    value: status,
    label: `INCOMING-GOODS.BUNDLE-GROUPS.STATUSES.${status}`,
}));

const BUNDLE_GROUPS_VALID_SORT_FIELDS = [
    'createdAt',
    'name',
    'status',
    'consignorName',
    'carrierName',
    'consigneeName',
];

export class ConsigneeBundleGroupsConfig {
    static create(deps: ConsigneeBundleGroupsConfigDeps): ViewConfig {
        return {
            id: 'consignee-bundlegroups',
            name: 'Bundle Groups',
            translationKey: 'OUTGOING-GOODS.TOGGLER.BUNDLE-GROUPS',
            type: 'bundlegroups',
            userRole: 'consignee',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey =
                        deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    return deps.consigneeBundleGroupsPaginatedService.loadConsigneeBundleGroups(
                        orgSiteKey,
                        BUNDLE_GROUPS_VALID_SORT_FIELDS
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        selectableCondition: (item: TableRowData) => {
                            const isNotFinished =
                                !hasStatus(item) ||
                                item.status !== 'checkin_finished';
                            const isNotReadOnly =
                                !hasReadOnly(item) || !item.read_only;
                            return isNotFinished && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.details(),
                    {
                        key: 'name',
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.NAME',
                        sortable: true,
                        filterable: false,
                    },
                    ColumnCreator.consignorName({
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CONSIGNOR',
                    }),
                    ColumnCreator.carrierName({
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CARRIER',
                    }),
                    ColumnCreator.consigneeName({
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CONSIGNEE',
                    }),
                    {
                        key: 'status',
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.STATUS',
                        sortable: true,
                        filterable: false,
                        component: BundleGroupStatusDisplayComponent,
                        componentInputs: (
                            row: TableRowData,
                            value: unknown
                        ) => ({
                            value,
                            row,
                        }),
                    },
                    {
                        key: 'bundleKeys',
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.FOLDER-COUNT',
                        sortable: false,
                        filterable: false,
                        formatter: (value: unknown) =>
                            Array.isArray(value) ? String(value.length) : '0',
                    },
                    {
                        key: 'deliveryNoteKeys',
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.DELIVERY-NOTE-COUNT',
                        sortable: false,
                        filterable: false,
                        formatter: (value: unknown) =>
                            Array.isArray(value) ? String(value.length) : '0',
                    },
                    ColumnCreator.date({
                        key: 'createdAt',
                        header: 'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CREATED-AT',
                        showTime: true,
                        sortable: true,
                        filterable: false,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    selectionMode: 'single',
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: BundleGroupData) =>
                        (item.bundleKeys?.length ?? 0) > 0,
                    tableConfig: {
                        columns: [
                            ColumnCreator.actions({
                                width: '200px',
                                configuration: {
                                    type: 'bundle',
                                    showDownload: true,
                                    showViewPdf: true,
                                    showDeliveryNotesCount: true,
                                    downloadEndpoint: (item: TableRowData) =>
                                        deps.actionsService.downloadDeliveryBundle(
                                            item as FolderData
                                        ),
                                    viewPdfEndpoint: (item: TableRowData) =>
                                        deps.actionsService.viewFolderAsPdf(
                                            item as FolderData
                                        ),
                                } as TableActionsConfig,
                            }),
                            ColumnCreator.bundleNumber({
                                sortable: false,
                                filterable: false,
                            }),
                            ColumnCreator.bundleName(),
                            ColumnCreator.consignorName({
                                header: 'INCOMING-GOODS.TABLE.CONSIGNOR-NAME',
                            }),
                            ColumnCreator.carrierName({
                                header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
                            }),
                            ColumnCreator.dateShipped({
                                header: 'INCOMING-GOODS.TABLE.DATE',
                            }),
                            ColumnCreator.status({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
                                showDiscrepanciesIcon: true,
                            }),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: BundleGroupData,
                                organizationSiteKey: string
                            ) => {
                                const bundleKeys = parentRow.bundleKeys || [];
                                const bundles = await Promise.all(
                                    bundleKeys.map(async bundleKey => {
                                        try {
                                            return await lastValueFrom(
                                                deps.deliveryNoteBundlesService.getConsigneeBundle(
                                                    {
                                                        deliveryNoteBundleKey:
                                                            bundleKey,
                                                        organizationSiteKey,
                                                    }
                                                )
                                            );
                                        } catch (error) {
                                            console.error(
                                                'Failed to load bundle of bundle group',
                                                bundleKey,
                                                error
                                            );
                                            return null;
                                        }
                                    })
                                );

                                const loaded = bundles.filter(
                                    (
                                        bundle
                                    ): bundle is ConsigneeDeliveryNoteBundle =>
                                        bundle !== null
                                );

                                const failedCount =
                                    bundles.length - loaded.length;
                                if (failedCount > 0) {
                                    deps.toastService.error(
                                        deps.translateService.instant(
                                            'INCOMING-GOODS.BUNDLE-GROUPS.BUNDLES-PARTIAL-LOAD-ERROR',
                                            { count: failedCount }
                                        )
                                    );
                                }

                                return loaded.map(bundle =>
                                    toGroupedBundleRow(bundle, parentRow)
                                );
                            },
                            organizationSiteKeyResolver: (
                                row: BundleGroupData
                            ) => row.orgSiteKey,
                        },
                        loadingMessage:
                            'INCOMING-GOODS.BUNDLE-GROUPS.LOADING-BUNDLES',
                        emptyMessage: 'INCOMING-GOODS.BUNDLE-GROUPS.NO-BUNDLES',
                        tableClass: 'expanded-delivery-notes-table',
                        maxHeight: '400px',
                    },
                },
            },
            filterConfig: {
                filterKeys: [
                    FilterKeyCreator.text(
                        'name',
                        'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.NAME'
                    ),
                    FilterKeyCreator.options(
                        'status',
                        'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.STATUS',
                        BUNDLE_GROUP_STATUS_FILTER_OPTIONS
                    ),
                    FilterKeyCreator.text(
                        'consignorName',
                        'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CONSIGNOR'
                    ),
                    FilterKeyCreator.text(
                        'carrierName',
                        'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CARRIER'
                    ),
                    FilterKeyCreator.text(
                        'consigneeName',
                        'INCOMING-GOODS.BUNDLE-GROUPS.TABLE.CONSIGNEE'
                    ),
                ],
            },
            searchConfig: {
                validSortFields: BUNDLE_GROUPS_VALID_SORT_FIELDS,
                defaultSortField: 'createdAt',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canView: true,
                canCreate: false,
                canUpdate: false,
                canDelete: false,
                canExport: false,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'checkinBundleGroup',
                        label: 'INCOMING-GOODS.BUTTONS.CHECKIN-BUNDLE-GROUP',
                        translationKey:
                            'INCOMING-GOODS.BUTTONS.CHECKIN-BUNDLE-GROUP',
                        svgIcon: 'folder-checkout',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: () => true,
                        disabled: () => {
                            const groups = filterBundleGroupData(
                                deps.selectionStore.selectedItems()
                            );
                            return (
                                groups.length !== 1 ||
                                groups[0].status === 'checkin_finished' ||
                                !!groups[0].read_only
                            );
                        },
                        handler: async () => {
                            const groups = filterBundleGroupData(
                                deps.selectionStore.selectedItems()
                            );
                            if (groups.length === 1) {
                                await deps.actionsService.checkinBundleGroup(
                                    groups[0]
                                );
                            }
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.CHECKIN-BUNDLE-GROUP',
                    },
                    {
                        id: 'ungroupBundleGroup',
                        label: 'INCOMING-GOODS.BUTTONS.UNGROUP-BUNDLE-GROUP',
                        translationKey:
                            'INCOMING-GOODS.BUTTONS.UNGROUP-BUNDLE-GROUP',
                        icon: 'layers_clear',
                        color: 'primary',
                        variant: 'stroked',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: () => true,
                        disabled: () => {
                            const groups = filterBundleGroupData(
                                deps.selectionStore.selectedItems()
                            );
                            return (
                                groups.length !== 1 ||
                                !deps.actionsService.canUngroupBundleGroup(
                                    groups[0]
                                )
                            );
                        },
                        handler: async () => {
                            const groups = filterBundleGroupData(
                                deps.selectionStore.selectedItems()
                            );
                            if (groups.length === 1) {
                                await deps.actionsService.ungroupBundleGroup(
                                    groups[0]
                                );
                            }
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.UNGROUP-BUNDLE-GROUP',
                    },
                ],
            },
        };
    }
}
